import BaseService from "../base";
import {keycloak} from "../../keycloak/keycloak";


const apiService = new BaseService(process.env.REACT_APP_VISUALS_API_URL, {});

// Cache for currencies to avoid repeated API calls
let cachedCurrencies = null;
let lastFetchTime = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

export async function fetchCurrencyDataByDate(date) {
    if (keycloak.authenticated) {
        let headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${keycloak.token}`,
            'Accept': 'application/json'
        }
        return await apiService.get( '/v1/currency', {date: date}, {headers: headers})
            .then(result => {
                return result.data
            })
            .catch(reason => {
                throw reason
            })
    }
    throw new Error('You are not authenticated')
}

/**
 * Fetches all currencies from the API
 * Uses caching to avoid repeated calls within CACHE_DURATION
 */
export async function fetchAllCurrencies(forceRefresh = false) {
    // Return cached data if it's still valid and not forcing refresh
    if (!forceRefresh && cachedCurrencies && (Date.now() - lastFetchTime) < CACHE_DURATION) {
        return cachedCurrencies;
    }

    if (keycloak.authenticated) {
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${keycloak.token}`,
            'Accept': 'application/json'
        };

        try {
            const response = await apiService.get( '/v1/currencies', {}, { headers });
            cachedCurrencies = response.data;
            lastFetchTime = Date.now();
            return cachedCurrencies;
        } catch (error) {
            console.error('Error fetching currencies:', error);
            throw error;
        }
    }
    throw new Error('You are not authenticated');
}

/**
 * Extracts unique bank clients from currencies
 */
export function getBankClients(currencies) {
    if (!currencies || !Array.isArray(currencies)) return [];
    const bankClients = new Set();
    currencies.forEach(currency => {
        if (currency.client) {
            bankClients.add(currency.client);
        }
    });
    
    return Array.from(bankClients).sort();
}

/**
 * Gets available dates for a specific bank client where toCurrency is EUR
 * Returns sorted array with newest dates first
 */
export function getAvailableDatesForBankClient(currencies, bankClient) {
    if (!currencies || !Array.isArray(currencies) || !bankClient) return [];
    // Filter currencies by bank client and toCurrency = 'EUR'
    const clientCurrencies = currencies.filter(
        currency => 
            currency.client === bankClient &&
            currency.toCurrency === 'EUR'
    );
    // Sort by date descending (newest first) and map to a cleaner format
    return clientCurrencies
        .map(currency => {
            // Use the correct field name from API: createdAtDate
            const dateField = currency.createdAtDate;
            
            // Validate the date
            const dateObj = dateField ? new Date(dateField) : null;
            const isValidDate = dateObj && !isNaN(dateObj.getTime());
            
            return {
                id: currency.id,
                date: dateField,
                displayDate: isValidDate 
                    ? dateObj.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    })
                    : 'Invalid Date',
                rate: currency.sell || currency.middle || 0
            };
        })
        .filter(item => item.date && item.displayDate !== 'Invalid Date') // Only keep items with valid dates
        .sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return dateB - dateA; // Newest first
        });
}

/**
 * Clears the currency cache (useful for forced refresh)
 */
export function clearCurrencyCache() {
    cachedCurrencies = null;
    lastFetchTime = 0;
}

